import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaTv, FaPlay, FaStopCircle, FaCheckCircle, FaExclamationTriangle, FaSpinner, FaHome } from 'react-icons/fa';
import { probeTv, sendToTv } from '../../services/cast';
import { getStoredPlayerId } from '../../services/players';

// ---------------------------------------------------------------------------
// Phone/tablet/desktop side of "Watch on TV".
// Opened from movie/series detail pages (and any "Watch on TV" action).
//  - enter the 6-digit code shown on the TV screen (once)
//  - live probe: green when the TV answers
//  - Play / Stop buttons; successful code is saved for one-tap next time
// ---------------------------------------------------------------------------

const CastModal = ({ open, item, watchOptions, tvCode, onSaveCode, onClose }) => {
  const navigate = useNavigate();
  const [code, setCode] = useState(tvCode || '');
  const [probing, setProbing] = useState(false);
  const [tvOnline, setTvOnline] = useState(null); // null = unknown
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const probeTimer = useRef(null);

  useEffect(() => {
    if (open) {
      setCode(tvCode || '');
      setError(null);
      setTvOnline(null);
    }
  }, [open, tvCode]);

  // Live probe when a full 6-digit code is entered (debounced).
  useEffect(() => {
    clearTimeout(probeTimer.current);
    const clean = String(code || '').replace(/\D/g, '');
    if (clean.length !== 6) {
      setTvOnline(null);
      return undefined;
    }
    setProbing(true);
    probeTimer.current = setTimeout(() => {
      probeTv(clean).then((online) => {
        setTvOnline(online);
        setProbing(false);
      });
    }, 500);
    return () => {
      clearTimeout(probeTimer.current);
      setProbing(false);
    };
  }, [code]);

  if (!open) return null;

  const title = item?.title || item?.original_title || item?.name || 'Selected title';

  const doSend = async (type) => {
    const clean = String(code || '').replace(/\D/g, '');
    if (clean.length !== 6) {
      setError('Enter the 6-digit code shown on the TV screen');
      return;
    }
    setSending(true);
    setError(null);
    try {
      let command;
      if (type === 'play') {
        command = {
          type: 'play',
          path: watchOptions?.path,
          title,
          poster_path: item?.poster_path || null,
          backdrop_path: item?.backdrop_path || null,
          season: watchOptions?.season ?? null,
          episode: watchOptions?.episode ?? null,
          player: getStoredPlayerId(),
        };
      } else if (type === 'connect') {
        // Pair only: TV leaves the code screen for the app Home; the phone
        // follows it there. Playing is a separate "Play on TV" action.
        command = { type: 'connect' };
      } else {
        command = { type: 'stop' };
      }

      const res = await sendToTv(clean, command);
      if (res.ok) {
        onSaveCode(clean);
        onClose();
        if (type === 'connect') navigate('/');
        else if (type === 'play') navigate('/remote');
      } else {
        setError('Send failed �?" check your internet connection');
      }
    } finally {
      setSending(false);
    }
  };

  const statusPill = () => {
    if (probing || (String(code).replace(/\D/g, '').length === 6 && tvOnline === null)) {
      return (
        <span className="inline-flex items-center gap-1.5 text-amber-400 text-xs font-bold">
          <FaSpinner className="animate-spin w-3 h-3" /> Checking for TV…
        </span>
      );
    }
    if (tvOnline === true) {
      return (
        <span className="inline-flex items-center gap-1.5 text-emerald-400 text-xs font-bold">
          <FaCheckCircle className="w-3 h-3" /> TV connected — ready!
        </span>
      );
    }
    if (tvOnline === false) {
      return (
        <span className="inline-flex items-center gap-1.5 text-red-400 text-xs font-bold">
          <FaExclamationTriangle className="w-3 h-3" /> No TV found — check the code / open the app on TV
        </span>
      );
    }
    return <span className="text-gray-500 text-xs">TV screen par dikh raha 6-digit code enter karein</span>;
  };

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="Watch on TV"
    >
      <div className="bg-dark-800 border border-white/10 rounded-3xl w-full max-w-sm p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-accent-gold/15 border border-accent-gold/30 flex items-center justify-center">
              <FaTv className="text-accent-gold w-5 h-5" />
            </div>
            <div>
              <h3 className="text-white font-black text-lg leading-tight">Watch on TV</h3>
              <p className="text-gray-400 text-xs">{title}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close cast dialog"
            className="p-2 rounded-xl bg-white/5 hover:bg-white/15 text-gray-300 hover:text-white transition-colors"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        {/* Code input */}
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000000"
          aria-label="TV pairing code"
          className="w-full bg-dark-900 border border-white/15 focus:border-accent-gold/60 rounded-2xl text-center text-3xl font-black tracking-[0.4em] text-white py-4 outline-none transition-colors placeholder:text-gray-600"
        />

        <div className="mt-3 min-h-[20px] text-center">{statusPill()}</div>

        {error && (
          <p className="mt-2 text-center text-red-400 text-xs font-semibold">{error}</p>
        )}

        {/* Actions */}
        <div className="mt-5 space-y-2.5">
          <button
            onClick={() => doSend(tvCode ? 'play' : 'connect')}
            disabled={sending}
            className="w-full inline-flex items-center justify-center gap-2 bg-accent-gold hover:bg-accent-goldHover disabled:opacity-60 text-dark-900 font-extrabold text-sm px-5 py-3.5 rounded-2xl transition-all shadow-lg"
          >
            {sending ? (
              <FaSpinner className="animate-spin w-4 h-4" />
            ) : tvCode ? (
              <FaPlay className="w-4 h-4" />
            ) : (
              <FaTv className="w-4 h-4" />
            )}
            <span>{tvCode ? 'Play on TV' : 'Connect TV'}</span>
          </button>

          {tvCode && (
            <>
              <button
                onClick={() => doSend('connect')}
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 disabled:opacity-60 text-white text-xs font-bold px-5 py-3 rounded-2xl border border-white/10 transition-colors"
              >
                <FaHome className="w-4 h-4 text-accent-gold" />
                <span>Show Home on TV</span>
              </button>
              <button
                onClick={() => doSend('stop')}
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 disabled:opacity-60 text-white text-xs font-bold px-5 py-3 rounded-2xl border border-white/10 transition-colors"
              >
                <FaStopCircle className="w-4 h-4 text-accent-red" />
                <span>Stop on TV</span>
              </button>
            </>
          )}
        </div>

        <p className="mt-4 text-center text-gray-500 text-[11px] leading-relaxed">
          {tvCode
            ? 'Play starts the video on TV and opens the remote on your phone.'
            : 'Connect pairs the TV and opens Home on both screens. TV and phone both need internet — same Wi-Fi is NOT required.'}
        </p>
        <button
          type="button"
          onClick={() => {
            if (String(code || '').replace(/\D/g, '').length === 6) onSaveCode(String(code).replace(/\D/g, ''));
            onClose();
            navigate('/remote');
          }}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/15 text-gray-200 text-xs font-bold py-2.5 rounded-xl border border-white/10 transition-colors"
        >
          <FaTv className="w-3.5 h-3.5 text-accent-gold" />
          <span>Open full Remote (pause / seek / HD / episodes)</span>
        </button>
      </div>
    </div>
  );
};

export default CastModal;
